import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { verifyCode, sendVerificationCode, setupRecaptcha } from '../../services/auth.service';
import { updateAuth } from '../../store/slices/authSlice';
import { setSwipeStats } from '../../store/slices/cacheSlice';
import { userService } from '../../services/userService';
import { useTranslation } from 'react-i18next';
import './Otp.css';
import Spinner from '../../components/common/Spinner';
import useTracking from '../../hooks/useTracking';
const OTP = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [focusedInput, setFocusedInput] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tempPhoneNumber = useSelector(state => state.auth.tempPhoneNumber);
    const uid = useSelector(state => state.auth.uid);
    const tracking = useTracking();
    // Redirect if no temporary number or already authenticated
    useEffect(() => {
        if (!tempPhoneNumber) {
            // '/auth/login' n'existe pas dans les routes : la vraie page de saisie du numéro est '/auth/phonelogin'
            navigate('/auth/phonelogin');
        } else if (uid) {
            navigate('/auth/welcome-policy');
        }
    }, [tempPhoneNumber, uid, navigate]);

    const handleChange = (index, value) => {
        if (value.length <= 1 && !isNaN(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value !== '' && index < 5) {
                inputRefs[index + 1].current.focus();
            }

            if (index === 5 && value !== '') {
                const allFilled = newOtp.every(digit => digit !== '');
                if (allFilled) {
                    handleVerifyCode(newOtp.join(''));
                }
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (index > 0 && otp[index] === '') {
                inputRefs[index - 1].current.focus();
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
            } else {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            }
        }
    };

    const handleFocus = (index) => {
        setFocusedInput(index);
        if (error) {
            setError('');
        }
    };

    const handleBlur = () => {
        setFocusedInput(null);
    };

    const handleResend = async () => {
        try {
            setLoading(true);
            setError('');

            // Réinitialiser le reCAPTCHA (celui de la page login n'existe plus dans le DOM)
            // puis renvoyer un nouveau code SMS au même numéro de téléphone.
            await setupRecaptcha('recaptcha-container-otp');
            await sendVerificationCode(tempPhoneNumber);

            setOtp(['', '', '', '', '', '']);
            inputRefs[0].current.focus();
        } catch (error) {
            console.error('Error resending code:', error);
            if (error.code === 'auth/too-many-requests') {
                setError(t('login.errors.tooManyRequests'));
            } else {
                setError(t('otp.errors.resendFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (code) => {
        if (!code || code.length !== 6) {
            setError(t('otp.errors.enterAllDigits'));
            return;
        }

        setError('');
        setLoading(true);

        try {
            const result = await verifyCode(code);
            const user = result.user;
            const token = await user.getIdToken();
            
            let existingUser = null;
            try {
                existingUser = await userService.getUserByFbid(user.uid, token);
            } catch (error) {
                if (error.response?.status !== 404) {
                    throw error;
                }
            }
            if (!existingUser) {
                const { fbp, fbc } = tracking.getFacebookCookies();
                const userData = {
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName,
                    profilePic: user.photoURL ? {
                        photoUrl: user.photoURL,
                        public_id: null
                    } : null, 
                    numero: tempPhoneNumber,
                    createdAt: new Date().toISOString(),
                    provider: 'phone',
                    level: 'free',
                    status: "Inactive",
                    invitations: 5,
                    ref: tracking.getLastRef(),
                    dailySwipes: 0,
                    lastSwipeDate: null,
                    weeklyInvitations: [],
                    acceptedInvitations: []
                };
                if (fbp) userData.fbp = fbp;
                if (fbc) userData.fbc = fbc;

                const response = await userService.createUser(userData, token);
                dispatch(updateAuth(response));
                navigate('/auth/welcome-policy');
                window.location.reload() 
            } else if (existingUser.status === "Inactive") {
                dispatch(updateAuth(existingUser));
                navigate('/auth/welcome-policy');
                window.location.reload() 
            } else {
                dispatch(updateAuth(existingUser));
                dispatch(setSwipeStats({
                    dailySwipes: existingUser.dailySwipes ? existingUser.dailySwipes : 0,
                    lastSwipeDate: existingUser.lastSwipeDate ? existingUser.lastSwipeDate : null
                }));
                navigate(existingUser.status === 'PendingDeletion' ? '/settings' : '/home');
                window.location.reload() 

            }
        } catch (error) {
            console.error('Error verifying code:', error);
            setError(t('otp.errors.invalidCode'));
            
            setOtp(['', '', '', '', '', '']);
            inputRefs[0].current.focus();
        } finally {
            setLoading(false);
        }
    };

    if (!tempPhoneNumber) {
        return null; 
    }

    return (
        <div className="otp-container">
            {loading && <Spinner />}
            <div className="page-content">
                <div className="container">
                    <div className="account-area style-2">
                        {/* Retour vers l'étape précédente de la logique du parcours (pas l'historique du navigateur : */}
                        {/* la redirection automatique de ProtectedAuthRoute peut remplacer l'historique réel) */}
                        <a href="#" onClick={(e) => { e.preventDefault(); navigate('/auth/phonelogin'); }} className="back-btn">
                            <ChevronLeft className="icon" />
                        </a>
                        <div className="section-head ps-0 d-flex align-items-center">
                            <h2 className="me-2 mb-0">{t('otp.title')}</h2>
                            <button 
                                onClick={handleResend} 
                                className="badge"
                                disabled={loading}
                            >
                                {t('otp.resend')}
                            </button>
                        </div>

                        <div id="otp" className="digit-group input-mini">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={inputRefs[index]}
                                    className={`form-control ${error && focusedInput !== index ? 'is-invalid' : ''}`}
                                    type="number"
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onFocus={() => handleFocus(index)}
                                    onBlur={handleBlur}
                                    disabled={loading}
                                    aria-label={t('otp.digitLabel', { number: index + 1 })}
                                />
                            ))}
                        </div>

                        {error && (
                            <div className="invalid-feedback d-block text-center mt-2">
                                {error}
                            </div>
                        )}
                        {/* Conteneur requis par Firebase pour le reCAPTCHA du renvoi de code, reste invisible */}
                        <div id="recaptcha-container-otp"></div>
                    </div>
                </div>
            </div>
            {/* Footer */}
            <div className="footer fixed bg-white p-b15">
                <div className="container">
                    <button 
                        onClick={() => handleVerifyCode(otp.join(''))}
                        className="btn btn-lg btn-gradient w-100 dz-flex-box btn-shadow rounded-xl"
                        disabled={otp.some(digit => digit === '') || loading}
                    >
                        {loading ? t('otp.verifying') : t('otp.verify')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OTP;
